import { supabase } from '@/lib/supabase'
import type {
  Faction,
  FactionWithParty,
  FactionWithMembers,
  FactionMembership,
  FactionMembershipWithCandidate,
  FactionStatus,
  FactionDemandType,
  IdeologyVector,
} from '@/types'

// Query key factory for consistent cache management
export const factionKeys = {
  all: ['factions'] as const,
  byParty: (partyId: string) => ['factions', 'party', partyId] as const,
  byNation: (nationId: string) => ['factions', 'nation', nationId] as const,
  detail: (id: string) => ['factions', 'id', id] as const,
  detailWithMembers: (id: string) => ['factions', 'detail', id] as const,
  byCandidate: (candidateId: string) => ['factions', 'candidate', candidateId] as const,
  memberships: (factionId: string) => ['factions', 'memberships', factionId] as const,
}

// ============================================================================
// Core Fetch Functions
// ============================================================================

/**
 * Fetches all factions belonging to a party
 * Orders by influence descending (most influential first)
 */
export async function fetchFactionsByParty(partyId: string): Promise<Faction[]> {
  const { data, error } = await supabase
    .from('factions')
    .select('*')
    .eq('party_id', partyId)
    .order('influence', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch factions for party: ${error.message}`)
  }

  return data as Faction[]
}

/**
 * Fetches a single faction with all its members
 * Joins faction_memberships with candidates to get member details
 */
export async function fetchFactionWithMembers(factionId: string): Promise<FactionWithMembers | null> {
  const { data, error } = await supabase
    .from('factions')
    .select(
      `
      *,
      faction_memberships (
        *,
        candidate:candidates (
          id,
          name,
          role,
          recognition,
          loyalty,
          ideology_vector
        )
      )
    `
    )
    .eq('id', factionId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      // Not found
      return null
    }
    throw new Error(`Failed to fetch faction: ${error.message}`)
  }

  // Transform the data to match FactionWithMembers interface
  const members = (data.faction_memberships || []).map((membership: FactionMembershipWithCandidate) => ({
    ...membership,
    candidate: membership.candidate,
  })) as FactionMembershipWithCandidate[]

  return {
    ...data,
    members,
    member_count: members.length,
  } as FactionWithMembers
}

/**
 * Fetches all factions across all parties in a nation
 * Joins with parties to include party name and color
 * Orders by party, then influence
 */
export async function fetchFactionsByNation(nationId: string): Promise<FactionWithParty[]> {
  const { data, error } = await supabase
    .from('factions')
    .select(
      `
      *,
      party:parties!inner (
        id,
        name,
        abbreviation,
        color,
        nation_id
      )
    `
    )
    .eq('party.nation_id', nationId)
    .order('party_id', { ascending: true })
    .order('influence', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch factions for nation: ${error.message}`)
  }

  return data as FactionWithParty[]
}

/**
 * Fetches the faction a candidate belongs to (if any)
 * Returns null if candidate is not in any faction
 */
export async function fetchCandidateFaction(candidateId: string): Promise<Faction | null> {
  const { data, error } = await supabase
    .from('faction_memberships')
    .select(
      `
      faction:factions (*)
    `
    )
    .eq('candidate_id', candidateId)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to fetch candidate faction: ${error.message}`)
  }

  if (!data || !data.faction) {
    return null
  }

  // Supabase returns array for joins, take first element
  const faction = Array.isArray(data.faction) ? data.faction[0] : data.faction
  return faction as Faction
}

/**
 * Fetches all memberships for a faction with candidate details
 * Orders by commitment_level descending
 */
export async function fetchFactionMemberships(
  factionId: string
): Promise<FactionMembershipWithCandidate[]> {
  const { data, error } = await supabase
    .from('faction_memberships')
    .select(
      `
      *,
      candidate:candidates (
        id,
        name,
        role,
        recognition,
        loyalty,
        ideology_vector
      )
    `
    )
    .eq('faction_id', factionId)
    .order('commitment_level', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch faction memberships: ${error.message}`)
  }

  return data as FactionMembershipWithCandidate[]
}

// ============================================================================
// Update Functions
// ============================================================================

/**
 * Updates the faction name (only field players can edit)
 * Validates name is not empty and reasonable length (max 50 chars)
 */
export async function updateFactionName(factionId: string, name: string): Promise<void> {
  // Validate name
  const trimmedName = name.trim()
  if (!trimmedName) {
    throw new Error('Faction name cannot be empty')
  }
  if (trimmedName.length > 50) {
    throw new Error('Faction name cannot exceed 50 characters')
  }

  const { error } = await supabase
    .from('factions')
    .update({ name: trimmedName })
    .eq('id', factionId)

  if (error) {
    throw new Error(`Failed to update faction name: ${error.message}`)
  }
}

/**
 * Adjusts faction satisfaction by the given amount (positive or negative)
 * Clamps result to 0-100 range
 */
export async function updateFactionSatisfaction(factionId: string, change: number): Promise<Faction> {
  // First fetch current satisfaction
  const { data: current, error: fetchError } = await supabase
    .from('factions')
    .select('satisfaction')
    .eq('id', factionId)
    .single()

  if (fetchError) {
    throw new Error(`Failed to fetch faction: ${fetchError.message}`)
  }

  // Clamp to 0-100 range
  const newSatisfaction = Math.max(0, Math.min(100, (current?.satisfaction ?? 50) + change))

  const { data, error } = await supabase
    .from('factions')
    .update({ satisfaction: newSatisfaction })
    .eq('id', factionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update faction satisfaction: ${error.message}`)
  }

  return data as Faction
}

/**
 * Updates faction status (dormant, active, demanding, threatening, defecting)
 */
export async function updateFactionStatus(factionId: string, status: FactionStatus): Promise<Faction> {
  const { data, error } = await supabase
    .from('factions')
    .update({ status })
    .eq('id', factionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update faction status: ${error.message}`)
  }

  return data as Faction
}

/**
 * Sets a demand on the faction
 * Updates demand_type, demand_details, and last_demand_tick
 */
export async function setFactionDemand(
  factionId: string,
  demandType: FactionDemandType,
  demandDetails: Record<string, unknown>,
  currentTick: number
): Promise<Faction> {
  const { data, error } = await supabase
    .from('factions')
    .update({
      demand_type: demandType,
      demand_details: demandDetails,
      last_demand_tick: currentTick,
    })
    .eq('id', factionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to set faction demand: ${error.message}`)
  }

  return data as Faction
}

/**
 * Clears the current demand (sets demand_type, demand_details to null)
 * Used when demand is met or faction status changes
 */
export async function clearFactionDemand(factionId: string): Promise<Faction> {
  const { data, error } = await supabase
    .from('factions')
    .update({
      demand_type: null,
      demand_details: null,
    })
    .eq('id', factionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to clear faction demand: ${error.message}`)
  }

  return data as Faction
}

// ============================================================================
// Calculation Functions
// ============================================================================

/**
 * Calculates faction's influence within the party
 * Formula:
 *   Base: (member_count / total_party_candidates) * 50
 *   Recognition bonus: sum of member recognition / 10
 *   Role bonus: +10 if faction has ministers, +5 for regional candidates
 *   Leader bonus: +15 if faction has a designated leader
 * Clamps result to 0-100
 */
export async function calculateFactionInfluence(factionId: string): Promise<number> {
  // Get faction with party_id and leader info
  const { data: faction, error: factionError } = await supabase
    .from('factions')
    .select('party_id, leader_candidate_id')
    .eq('id', factionId)
    .single()

  if (factionError) {
    throw new Error(`Failed to fetch faction: ${factionError.message}`)
  }

  // Get faction members with their details
  const { data: memberships, error: memberError } = await supabase
    .from('faction_memberships')
    .select(
      `
      candidate:candidates (
        id,
        recognition,
        role
      )
    `
    )
    .eq('faction_id', factionId)

  if (memberError) {
    throw new Error(`Failed to fetch faction members: ${memberError.message}`)
  }

  // Get total party candidates count
  const { count: totalCandidates, error: countError } = await supabase
    .from('candidates')
    .select('*', { count: 'exact', head: true })
    .eq('party_id', faction.party_id)
    .eq('is_active', true)

  if (countError) {
    throw new Error(`Failed to count party candidates: ${countError.message}`)
  }

  const memberCount = memberships?.length || 0
  const totalPartyCandidates = totalCandidates || 0

  // Calculate base influence from member proportion
  const baseInfluence =
    totalPartyCandidates > 0 ? (memberCount / totalPartyCandidates) * 50 : 0

  // Calculate recognition bonus
  let recognitionSum = 0
  let hasMinisters = false
  let hasRegionalCandidates = false

  for (const membership of memberships || []) {
    // Supabase returns array for joins, take first element
    const candidateData = membership.candidate
    const candidate = (Array.isArray(candidateData) ? candidateData[0] : candidateData) as
      | { id: string; recognition: number; role: string }
      | null
    if (candidate) {
      recognitionSum += candidate.recognition || 0
      if (candidate.role === 'minister') hasMinisters = true
      if (candidate.role === 'regional_candidate') hasRegionalCandidates = true
    }
  }

  const recognitionBonus = recognitionSum / 10

  // Role bonuses
  let roleBonus = 0
  if (hasMinisters) roleBonus += 10
  if (hasRegionalCandidates) roleBonus += 5

  // Leader bonus
  const leaderBonus = faction.leader_candidate_id ? 15 : 0

  // Total (clamped to 0-100)
  const total = baseInfluence + recognitionBonus + roleBonus + leaderBonus
  return Math.min(100, Math.max(0, Math.round(total)))
}

/**
 * Calculates the average ideology vector of all faction members
 * Returns null if faction has no members
 */
export async function calculateFactionIdeologyCenter(factionId: string): Promise<IdeologyVector | null> {
  const { data: memberships, error } = await supabase
    .from('faction_memberships')
    .select(
      `
      candidate:candidates (
        ideology_vector
      )
    `
    )
    .eq('faction_id', factionId)

  if (error) {
    throw new Error(`Failed to fetch faction members: ${error.message}`)
  }

  if (!memberships || memberships.length === 0) {
    return null
  }

  // Collect all ideology vectors
  const ideologies: IdeologyVector[] = []
  for (const membership of memberships) {
    // Supabase returns array for joins, take first element
    const candidateData = membership.candidate
    const candidate = (Array.isArray(candidateData) ? candidateData[0] : candidateData) as
      | { ideology_vector: IdeologyVector }
      | null
    if (candidate?.ideology_vector) {
      ideologies.push(candidate.ideology_vector)
    }
  }

  if (ideologies.length === 0) {
    return null
  }

  // Calculate average
  const center: IdeologyVector = [0, 0, 0, 0, 0, 0, 0]

  for (const ideology of ideologies) {
    for (let i = 0; i < 7; i++) {
      center[i] += ideology[i]
    }
  }

  for (let i = 0; i < 7; i++) {
    center[i] = Math.round(center[i] / ideologies.length)
  }

  return center
}

/**
 * Recalculates both influence and ideology_vector
 * Updates the faction record with new values
 * Called after membership changes
 */
export async function recalculateFactionMetrics(factionId: string): Promise<Faction> {
  const [influence, ideologyVector] = await Promise.all([
    calculateFactionInfluence(factionId),
    calculateFactionIdeologyCenter(factionId),
  ])

  const { data, error } = await supabase
    .from('factions')
    .update({
      influence,
      ideology_vector: ideologyVector,
    })
    .eq('id', factionId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update faction metrics: ${error.message}`)
  }

  return data as Faction
}

// ============================================================================
// Membership Management Functions
// ============================================================================

/**
 * Adds a candidate to a faction
 * Sets joined_tick to currentTick
 * Sets commitment_level to initialCommitment (default 50)
 * Sets will_defect_with_faction to false initially
 * Triggers recalculation of faction metrics
 */
export async function addCandidateToFaction(
  factionId: string,
  candidateId: string,
  currentTick: number,
  initialCommitment: number = 50
): Promise<FactionMembership> {
  // Ensure candidate isn't already in a faction
  const { data: existing, error: checkError } = await supabase
    .from('faction_memberships')
    .select('id')
    .eq('candidate_id', candidateId)
    .maybeSingle()

  if (checkError) {
    throw new Error(`Failed to check existing membership: ${checkError.message}`)
  }

  if (existing) {
    throw new Error('Candidate is already a member of a faction')
  }

  // Create the membership
  const { data, error } = await supabase
    .from('faction_memberships')
    .insert({
      faction_id: factionId,
      candidate_id: candidateId,
      joined_tick: currentTick,
      commitment_level: Math.max(0, Math.min(100, initialCommitment)),
      will_defect_with_faction: false,
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to add candidate to faction: ${error.message}`)
  }

  // Recalculate faction metrics
  await recalculateFactionMetrics(factionId)

  return data as FactionMembership
}

/**
 * Removes a candidate from their faction (if any)
 * Triggers recalculation of faction metrics for the affected faction
 * No-op if candidate is not in a faction
 */
export async function removeCandidateFromFaction(candidateId: string): Promise<void> {
  // First find which faction they're in
  const { data: membership, error: findError } = await supabase
    .from('faction_memberships')
    .select('id, faction_id')
    .eq('candidate_id', candidateId)
    .maybeSingle()

  if (findError) {
    throw new Error(`Failed to find membership: ${findError.message}`)
  }

  if (!membership) {
    // Not in a faction, nothing to do
    return
  }

  // Delete the membership
  const { error: deleteError } = await supabase
    .from('faction_memberships')
    .delete()
    .eq('id', membership.id)

  if (deleteError) {
    throw new Error(`Failed to remove candidate from faction: ${deleteError.message}`)
  }

  // Recalculate faction metrics
  await recalculateFactionMetrics(membership.faction_id)
}

/**
 * Adjusts commitment_level by the given amount
 * Clamps to 0-100 range
 * Updates will_defect_with_faction based on new commitment:
 *   commitment >= 70: will_defect = true
 *   commitment < 50: will_defect = false
 *   Between 50-70: no change
 */
export async function updateMembershipCommitment(
  membershipId: string,
  commitmentChange: number
): Promise<FactionMembership> {
  // First fetch current values
  const { data: current, error: fetchError } = await supabase
    .from('faction_memberships')
    .select('commitment_level, will_defect_with_faction')
    .eq('id', membershipId)
    .single()

  if (fetchError) {
    throw new Error(`Failed to fetch membership: ${fetchError.message}`)
  }

  // Calculate new commitment (clamped)
  const newCommitment = Math.max(0, Math.min(100, (current?.commitment_level ?? 50) + commitmentChange))

  // Determine will_defect based on thresholds
  let willDefect = current?.will_defect_with_faction ?? false
  if (newCommitment >= 70) {
    willDefect = true
  } else if (newCommitment < 50) {
    willDefect = false
  }
  // Between 50-70: no change

  const { data, error } = await supabase
    .from('faction_memberships')
    .update({
      commitment_level: newCommitment,
      will_defect_with_faction: willDefect,
    })
    .eq('id', membershipId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update membership commitment: ${error.message}`)
  }

  return data as FactionMembership
}

// ============================================================================
// Bulk Operations (for tick processing)
// ============================================================================

/**
 * Fetches all non-dormant factions in the nation
 * Used by tick processor to know which factions need satisfaction updates
 */
export async function getFactionsRequiringUpdate(nationId: string): Promise<Faction[]> {
  const { data, error } = await supabase
    .from('factions')
    .select(
      `
      *,
      party:parties!inner (
        nation_id
      )
    `
    )
    .eq('party.nation_id', nationId)
    .neq('status', 'dormant')

  if (error) {
    throw new Error(`Failed to fetch factions requiring update: ${error.message}`)
  }

  return data as Faction[]
}

/**
 * Efficiently updates multiple factions' satisfaction in one operation
 * Used by tick processor for batch updates
 */
export async function bulkUpdateFactionSatisfaction(
  updates: Array<{ factionId: string; change: number }>
): Promise<void> {
  if (updates.length === 0) return

  // Supabase doesn't support bulk updates with different values per row easily,
  // so we need to do individual updates. For better performance in production,
  // consider using an RPC function.
  const promises = updates.map(async ({ factionId, change }) => {
    // Fetch current satisfaction
    const { data: current, error: fetchError } = await supabase
      .from('factions')
      .select('satisfaction')
      .eq('id', factionId)
      .single()

    if (fetchError) {
      throw new Error(`Failed to fetch faction ${factionId}: ${fetchError.message}`)
    }

    // Calculate new satisfaction (clamped)
    const newSatisfaction = Math.max(0, Math.min(100, (current?.satisfaction ?? 50) + change))

    return supabase
      .from('factions')
      .update({ satisfaction: newSatisfaction })
      .eq('id', factionId)
  })

  const results = await Promise.all(promises)

  // Check for any errors
  const errors = results.filter((r) => r.error)
  if (errors.length > 0) {
    throw new Error(`Failed to update factions: ${errors[0].error?.message}`)
  }
}
